import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Cloud, Sun, CloudRain, Eye, CloudSun } from "lucide-react";
import { getObraStats } from "@/services/obraStats";
import { parseYmdLocal, formatYmd } from "@/utils/dateUtils";

// Linha real: clima registrado em cada RDO da obra (manhã/tarde)
interface WeatherRow {
  date: string; // yyyy-mm-dd
  dayOfWeek: string;
  morningCondition?: string;
  morningWorkable?: boolean;
  afternoonCondition?: string;
  afternoonWorkable?: boolean;
  observations: string;
}

interface WeatherHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Obra cujos RDOs alimentam o histórico */
  obraId?: string;
}

const getWeatherIcon = (condition?: string) => {
  switch (condition) {
    case "sunny":
      return <Sun className="w-4 h-4 text-yellow-500" />;
    case "cloudy":
      return <Cloud className="w-4 h-4 text-gray-500" />;
    case "rainy":
    case "stormy":
      return <CloudRain className="w-4 h-4 text-blue-500" />;
    case "foggy":
      return <Eye className="w-4 h-4 text-gray-400" />;
    default:
      return <Cloud className="w-4 h-4 text-muted-foreground/50" />;
  }
};

const getConditionLabel = (condition?: string) => {
  switch (condition) {
    case "sunny": return "Ensolarado";
    case "cloudy": return "Nublado";
    case "rainy": return "Chuvoso";
    case "stormy": return "Tempestade";
    case "foggy": return "Neblina";
    case undefined:
    case "": return "—";
    default: return condition;
  }
};

const getWorkabilityBadge = (workable?: boolean) => {
  if (workable === undefined) return <span className="text-muted-foreground">—</span>;
  return workable ? (
    <Badge className="bg-green-100 text-green-800">Praticável</Badge>
  ) : (
    <Badge className="bg-red-100 text-red-800">Impraticável</Badge>
  );
};

export function WeatherHistoryModal({ open, onOpenChange, obraId }: WeatherHistoryModalProps) {
  const [rows, setRows] = useState<WeatherRow[]>([]);

  useEffect(() => {
    if (!open || !obraId) return;
    let active = true;
    getObraStats(obraId)
      .then((s) => {
        if (!active) return;
        setRows(
          s.rdos.map((rdo) => ({
            date: rdo.date,
            dayOfWeek: parseYmdLocal(rdo.date).toLocaleDateString("pt-BR", { weekday: "long" }),
            morningCondition: rdo.weather?.morning?.condition,
            morningWorkable: rdo.weather?.morning?.workable,
            afternoonCondition: rdo.weather?.afternoon?.condition,
            afternoonWorkable: rdo.weather?.afternoon?.workable,
            observations: [rdo.weather?.morning?.observation, rdo.weather?.afternoon?.observation]
              .filter(Boolean)
              .join(" / "),
          }))
        );
      })
      .catch((e) => console.error("WeatherHistoryModal:", e));
    return () => { active = false; };
  }, [open, obraId]);

  const impracticaveis = rows.filter(
    (d) => d.morningWorkable === false || d.afternoonWorkable === false
  ).length;
  const praticaveis = rows.length - impracticaveis;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[95vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CloudSun className="w-5 h-5" />
            Histórico de Condições Climáticas
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col gap-4 overflow-hidden">
          {/* Resumo */}
          <div className="grid grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">{rows.length}</div>
              <div className="text-sm text-muted-foreground">Dias registrados</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{praticaveis}</div>
              <div className="text-sm text-muted-foreground">Dias praticáveis</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{impracticaveis}</div>
              <div className="text-sm text-muted-foreground">Dias impraticáveis</div>
            </div>
          </div>

          {/* Tabela */}
          <div className="flex-1 overflow-auto border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Dia da Semana</TableHead>
                  <TableHead>Manhã</TableHead>
                  <TableHead className="text-center">Praticável (manhã)</TableHead>
                  <TableHead>Tarde</TableHead>
                  <TableHead className="text-center">Praticável (tarde)</TableHead>
                  <TableHead>Observações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                      Nenhum registro de clima ainda. Preencha as condições climáticas nos RDOs.
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((day) => (
                  <TableRow key={day.date} className="hover:bg-muted/30">
                    <TableCell className="font-medium">{formatYmd(day.date)}</TableCell>
                    <TableCell className="capitalize">{day.dayOfWeek}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getWeatherIcon(day.morningCondition)}
                        <span>{getConditionLabel(day.morningCondition)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">{getWorkabilityBadge(day.morningWorkable)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getWeatherIcon(day.afternoonCondition)}
                        <span>{getConditionLabel(day.afternoonCondition)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">{getWorkabilityBadge(day.afternoonWorkable)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{day.observations || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
